import { createGlobalStyle } from 'styled-components';
import { alpha } from '@mui/material/styles';
import theme, {
  FONT_SIZES,
  FONT_WEIGHTS,
  NEUTRAL,
  PRIMARY,
} from '../../Theme';

const p = theme.palette;

const olive = p.brand.olive.main;
const oliveDark = p.brand.olive.dark;
const oliveTint = p.brand.olive.light;
const caramel = p.brand.caramel.main;
const caramelDark = p.brand.caramel.dark;
const caramelTint = p.brand.caramel.light;
const copper = p.brand.copper.main;
const copperDark = p.brand.copper.dark;
const copperTint = p.brand.copper.light;

const { functional: fn, brand: { coral } } = p;

/** Ink-based shadows for payment/pricing (avoid raw rgba in component styles). */
const ink = p.text.primary;
const radiusStandard = `${theme.radius.standard}px`;
const radiusPill = `${theme.radius.pill}px`;

const GlobalStyles = createGlobalStyle`
  :root {
    /* Canvas + type — warm neutrals (see Theme.js TOKENS + palette) */
    --background-light: ${p.background.default};
    --background-paper: ${p.background.paper};
    --background-soft: ${p.background.soft};
    --background-dark: ${p.background.dark};
    --surface: ${NEUTRAL.surface};
    --border-subtle: ${p.border?.subtle || p.divider};
    --border-hover: ${p.grey[300]};

    /* Radius tiers — standard UI surfaces + intentionally pill-shaped chips/tags */
    --radius-ui: ${radiusStandard};
    --radius-pill: ${radiusPill};
    --search-field-border-radius: var(--radius-ui);

    /* Brand CTA — premium ink (contained buttons, decisive emphasis) */
    --primary-color: ${PRIMARY.main};
    --primary-hover: ${PRIMARY.hover};
    --primary-light: ${PRIMARY.light};
    --primary-contrast: ${p.primary.contrastText};

    /* Type — warm plum ink (Theme TOKENS text.*) */
    --text-color: ${p.text.primary};
    --text-secondary: ${p.text.secondary};
    --text-muted: ${p.text.muted};
    --text-disabled: ${p.text.disabled};
    --heading-color: ${p.grey[900]};
    --font-heading: ${FONT_SIZES.heading};
    --font-body: ${FONT_SIZES.body};
    --font-caption: ${FONT_SIZES.caption};
    --font-display: ${FONT_SIZES.display};
    --font-title: ${FONT_SIZES.title};
    --font-body-sm: ${FONT_SIZES.bodySm};
    --font-micro: ${FONT_SIZES.micro};
    --font-weight-regular: ${FONT_WEIGHTS.regular};
    --font-weight-medium: ${FONT_WEIGHTS.medium};
    --font-weight-strong: ${FONT_WEIGHTS.strong};

    --action-hover: ${p.action.hover};
    --action-selected: ${p.action.selected};
    --action-selected-hover: ${p.action.selectedHover};

    --secondary-color: ${p.secondary.main};
    --secondary-contrast: ${p.secondary.contrastText};

    --error-color: ${p.error.main};
    --success-color: ${p.success.main};
    --warning-color: ${p.warning.main};
    --info-color: ${p.info.main};

    /* Workflow / collaboration status badges (applications, invites, campaign chips) */
    --workflow-positive-bg: ${p.workflowStatus.positive.bg};
    --workflow-positive-color: ${p.workflowStatus.positive.color};
    --workflow-committed-bg: ${p.workflowStatus.committed.bg};
    --workflow-committed-color: ${p.workflowStatus.committed.color};
    --workflow-negotiating-bg: ${p.workflowStatus.negotiating.bg};
    --workflow-negotiating-color: ${p.workflowStatus.negotiating.color};
    --workflow-negative-bg: ${p.workflowStatus.negative.bg};
    --workflow-negative-color: ${p.workflowStatus.negative.color};
    --workflow-canceled-bg: ${p.workflowStatus.canceled.bg};
    --workflow-canceled-color: ${p.workflowStatus.canceled.color};
    --workflow-neutral-bg: ${p.workflowStatus.neutral.bg};
    --workflow-neutral-color: ${p.workflowStatus.neutral.color};
    --workflow-completed-bg: ${p.workflowStatus.completed.bg};
    --workflow-completed-color: ${p.workflowStatus.completed.color};

    /* Brand ramps — legacy names preserved for styled-components call sites */
    --brand-olive: ${olive};
    --brand-olive-hover: ${oliveDark};
    --brand-olive-tint: ${oliveTint};
    --brand-functional: ${fn.main};
    --brand-functional-hover: ${fn.dark};
    --brand-functional-tint: ${fn.light};
    --brand-coral: ${coral.main};
    --brand-coral-hover: ${coral.dark};
    --brand-coral-tint: ${coral.light};
    --brand-forest: ${p.brand.forest.main};
    --brand-cream: ${p.brand.cream.main};
    --brand-caramel: ${caramel};
    --brand-caramel-hover: ${caramelDark};
    --brand-caramel-tint: ${caramelTint};
    --brand-copper: ${copper};
    --brand-copper-hover: ${copperDark};
    --brand-copper-tint: ${copperTint};

    /**
     * Semantic accents — neutral-first; ink = conversion, plum = system chrome, blush = creator emphasis
     * - link: infrastructure plum — inline links, FAQ nav, focus affordance
     * - soft: sand (caramel token) — warm secondary emphasis
     * - conversion: ink family — payment ribbons, primary money moments
     */
    --link-color: ${fn.main};
    --link-hover: ${fn.dark};
    --link-tint: ${fn.light};
    --accent-soft: ${caramel};
    --accent-soft-hover: ${caramelDark};
    --accent-soft-tint: ${caramelTint};
    --accent-conversion: ${copper};
    --accent-conversion-hover: ${copperDark};
    --accent-conversion-tint: ${copperTint};
    --live-color: ${p.live.main};
    --live-tint: ${p.live.light};

    --surface-muted: ${p.chat.surfaceMuted};

    /* Deprecated token names — map to current system (remove call sites over time) */
    --accent-pink-strong: ${coral.main};
    --accent-pink-strong-hover: ${coral.dark};
    --accent-pink-strong-tint: ${coral.light};
    --accent-blue: ${fn.main};
    --accent-blue-hover: ${fn.dark};
    --accent-blue-tint: ${fn.light};
    --accent-pink: ${coral.main};
    --accent-pink-light: ${coral.light};

    /**
     * Payment / pricing — elevations + CTAs (reference only via var(--*) in Payment styles)
     */
    --shadow-payment-card: 0 4px 12px ${alpha(ink, 0.06)};
    --shadow-plan-card: 0 3px 18px ${alpha(ink, 0.035)};
    --shadow-plan-card-strong: 0 8px 28px ${alpha(ink, 0.06)};
    --shadow-plan-card-hover: 0 6px 22px ${alpha(ink, 0.07)};
    --shadow-plan-card-hover-alt: 0 8px 32px ${alpha(ink, 0.08)};
    --shadow-dialog-elevated: 0 8px 32px ${alpha(ink, 0.1)};
    --shadow-compare-sticky: 4px 0 12px ${alpha(ink, 0.04)};
    --payment-dialog-gradient: linear-gradient(
      135deg,
      ${p.background.default} 0%,
      ${p.background.paper} 100%
    );
    /* Pricing plan primary actions — match app primary ink */
    --payment-cta-bg: ${PRIMARY.main};
    --payment-cta-color: ${p.primary.contrastText};
    --payment-banner-accent-border: ${fn.main};
    --table-row-hover-bg: ${p.action.hover};
    --error-contrast-text: ${p.error.contrastText};
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    overflow-x: hidden;
  }

  body {
    font-family: 'Inter', 'Poppins', system-ui, sans-serif;
    background-color: var(--background-light);
    color: var(--text-color);
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
  }

  #root {
    background-color: var(--background-light);
    min-height: 100vh;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  button {
    font-family: 'Inter', 'Poppins', system-ui, sans-serif;
    cursor: pointer;
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideInFromLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes slideInFromRight {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -200px 0;
    }
    100% {
      background-position: calc(200px + 100%) 0;
    }
  }

  .skeleton-shimmer {
    background: linear-gradient(
      90deg,
      var(--surface-muted) 25%,
      var(--border-subtle) 50%,
      var(--surface-muted) 75%
    );
    background-size: 200px 100%;
    animation: shimmer 1.5s infinite;
  }
`;

export default GlobalStyles;
